import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProvince } from '../hooks/useProvince';
import { useToast } from '../hooks/useToast';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { Spinner } from '../components/Spinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { CrystalBallResult } from '../components/CrystalBallResult';
import { CrystalEyeResult } from '../components/CrystalEyeResult';
import { MobileMagicShell } from '../components/magic/MobileMagicShell';
import { DesktopMagicShell } from '../components/magic/DesktopMagicShell';
import {
  calculateRuneCost,
  filterSpellsByCategory,
  getSpellByType,
  groupSpellsForOptgroups,
  INTEL_SPELL_TYPES,
  type SpellOptgroup,
} from '../constants/spells';
import { castSpell, getKingdom } from '../api/magic';
import type { SpellType, Kingdom, KingdomMember, CastSpellResult } from '../types/magic';
import type { Province } from '../types/province';
import { formatProtectionCountdown } from '../utils/protection';

/**
 * SF-148: named, fully-typed props shared by MobileMagicShell/DesktopMagicShell — same
 * ExploreShellProps/SciencesShellProps precedent. State stays hoisted here (not per-shell) so a
 * breakpoint flip mid-edit doesn't lose the in-progress target/spell selection.
 */
export interface MagicShellProps {
  province: Province;
  magicData: { wizards: number; currentRunes: number; currentMana: number; wpa: string };
  kingdomNumber: string;
  islandNumber: string;
  setKingdomNumber: (v: string) => void;
  setIslandNumber: (v: string) => void;
  handleChangeKingdom: () => Promise<void>;
  isLoadingKingdom: boolean;
  kingdomMembers: KingdomMember[];
  selectedTarget: string;
  handleTargetChange: (id: string) => void;
  selectedTargetLabel: string;
  selectedTargetProtectionCountdown: string | null;
  spellGroups: SpellOptgroup[];
  selectedSpell: SpellType | '';
  handleSpellChange: (t: SpellType | '') => void;
  runeCost: number;
  isSubmitting: boolean;
  submitError: string | null;
  successMessage: string | null;
  castBlockedReason: string | null;
  handleCastSpell: () => Promise<void>;
  crystalBallResult: CastSpellResult | null;
}

export function Magic() {
  const { provinceId, worldId } = useAuth();
  const { province, isLoading, error, refresh } = useProvince();
  const [searchParams] = useSearchParams();
  const { addToast } = useToast();
  const platform = useAppShellPlatform();

  const [kingdomNumber, setKingdomNumber] = useState('');
  const [islandNumber, setIslandNumber] = useState('');
  const [selectedTarget, setSelectedTarget] = useState('');
  const [kingdomMembers, setKingdomMembers] = useState<KingdomMember[]>([]);
  const [isLoadingKingdom, setIsLoadingKingdom] = useState(false);
  const [kingdomCache, setKingdomCache] = useState<Record<string, KingdomMember[]>>({});
  const [hasInitialized, setHasInitialized] = useState(false);

  const [selectedSpell, setSelectedSpell] = useState<SpellType | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [crystalBallResult, setCrystalBallResult] = useState<CastSpellResult | null>(null);

  // SF-139/140 precedent (mirrors Explore.tsx/Science.tsx): mobile routes feedback through the
  // app-wide toast system; desktop keeps its existing inline messages.
  const reportError = (message: string) => {
    if (platform === 'mobile') {
      addToast(message, 'failure');
    } else {
      setSubmitError(message);
    }
  };

  const reportSuccess = (message: string) => {
    if (platform === 'mobile') {
      addToast(message, 'success');
    } else {
      setSuccessMessage(message);
    }
  };

  // SF-181 Component 5: desktop has no 'warning' toast lane (inline submitError/successMessage
  // only) — route a warning-tone outcome through the same inline successMessage slot on desktop,
  // matching this file's existing platform split.
  const reportWarning = (message: string) => {
    if (platform === 'mobile') {
      addToast(message, 'warning');
    } else {
      setSuccessMessage(message);
    }
  };

  // SF-148 Component 1 (Phase 2 Critical Issue): consolidates what used to be two racing
  // useEffects. A deep-link target (?targetId=&targetLoc=) takes precedence over auto-loading
  // the player's own kingdom — the old ordering silently overwrote a deep-linked target's
  // kingdom with the player's own, breaking the Intel Hub → cast cross-page flow and making the
  // protection gate a no-op (selectedTargetMember resolved null since the target wasn't in the
  // wrong kingdom's member list).
  useEffect(() => {
    async function initializeKingdom() {
      if (!province || hasInitialized || !worldId) return;
      setHasInitialized(true);

      const targetId = searchParams.get('targetId');
      const targetLoc = searchParams.get('targetLoc');
      const targetLocParts = targetLoc ? targetLoc.split(':') : null;
      const hasDeepLinkTarget = Boolean(targetId && targetLocParts && targetLocParts.length === 2);

      let kingdomId: string;
      if (hasDeepLinkTarget && targetLocParts) {
        setKingdomNumber(targetLocParts[0]);
        setIslandNumber(targetLocParts[1]);
        kingdomId = targetLoc as string;
      } else {
        const { island, kingdom } = province.metadata.location;
        setIslandNumber(String(island));
        setKingdomNumber(String(kingdom));
        kingdomId = `${kingdom}:${island}`;
      }

      if (kingdomCache[kingdomId]) {
        setKingdomMembers(kingdomCache[kingdomId]);
        if (hasDeepLinkTarget && targetId) setSelectedTarget(targetId);
        return;
      }

      setIsLoadingKingdom(true);
      try {
        const kingdomData: Kingdom = await getKingdom(worldId, kingdomId);
        setKingdomMembers(kingdomData.members);
        setKingdomCache((prev) => ({ ...prev, [kingdomId]: kingdomData.members }));
        if (hasDeepLinkTarget && targetId) setSelectedTarget(targetId);
      } catch (err) {
        console.error('Failed to auto-load kingdom:', err);
      } finally {
        setIsLoadingKingdom(false);
      }
    }

    initializeKingdom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [province, hasInitialized, kingdomCache, worldId]);

  const magicData = useMemo(() => {
    if (!province) return null;
    const wizards = province.population.wizards;
    const currentRunes = province.resources.runes;
    const currentMana = province.resources.mana;
    const totalAcres = province.territory.totals.acres;
    const wpa = totalAcres > 0 ? (wizards / totalAcres).toFixed(2) : '0.00';
    return { wizards, currentRunes, currentMana, wpa };
  }, [province]);

  const handleChangeKingdom = async () => {
    setSubmitError(null);
    setSuccessMessage(null);

    if (!islandNumber || !kingdomNumber) {
      reportError('Please enter both island and kingdom numbers');
      return;
    }
    if (!worldId) {
      reportError('World ID not found');
      return;
    }

    const kingdomId = `${kingdomNumber}:${islandNumber}`;

    if (kingdomCache[kingdomId]) {
      setKingdomMembers(kingdomCache[kingdomId]);
      setSelectedTarget('');
      return;
    }

    setIsLoadingKingdom(true);
    try {
      const kingdom: Kingdom = await getKingdom(worldId, kingdomId);
      setKingdomMembers(kingdom.members);
      setKingdomCache({ ...kingdomCache, [kingdomId]: kingdom.members });
      setSelectedTarget('');
    } catch (err) {
      reportError(err instanceof Error ? err.message : 'Failed to load kingdom');
      setKingdomMembers([]);
    } finally {
      setIsLoadingKingdom(false);
    }
  };

  const availableSpells = useMemo(() => {
    if (!province) return [];
    const isSelfCast = selectedTarget === '';
    const category = isSelfCast ? 'SELF' : 'OFFENSIVE';
    const spells = filterSpellsByCategory(category, province.metadata.race);

    const isOwnKingdom = kingdomMembers.some((m) => m.provinceId === provinceId);
    if (!isSelfCast && isOwnKingdom) {
      const selfSpells = filterSpellsByCategory('SELF', province.metadata.race);
      return [...selfSpells, ...spells];
    }
    return spells;
  }, [selectedTarget, province, kingdomMembers, provinceId]);

  const spellGroups = useMemo(() => groupSpellsForOptgroups(availableSpells), [availableSpells]);

  const selectedTargetMember = kingdomMembers.find((m) => m.provinceId === selectedTarget) ?? null;
  const selectedTargetProtectionCountdown = selectedTargetMember
    ? formatProtectionCountdown(selectedTargetMember.protectionExpiresAtTick, province?.sync.currentTick ?? 0)
    : null;

  const selectedTargetLabel =
    selectedTarget === ''
      ? 'Self-Cast'
      : selectedTargetMember
        ? `${selectedTargetMember.provinceName} (${selectedTargetMember.location})`
        : 'Unknown target';

  const runeCost = useMemo(() => {
    if (!selectedSpell || !province) return 0;
    const spell = getSpellByType(selectedSpell);
    if (!spell) return 0;
    return calculateRuneCost(province.territory.totals.acres, spell);
  }, [selectedSpell, province]);

  const castBlockedReason: string | null = !selectedSpell
    ? 'Select a spell to cast'
    : selectedTargetProtectionCountdown && !INTEL_SPELL_TYPES.includes(selectedSpell)
      ? 'Target is protected — offensive spells cannot target this province yet'
      : null;

  const handleTargetChange = (id: string) => {
    setSelectedTarget(id);
    setSelectedSpell('');
    setCrystalBallResult(null);
  };

  const handleSpellChange = (t: SpellType | '') => {
    setSelectedSpell(t);
    setCrystalBallResult(null);
  };

  const handleCastSpell = async () => {
    setSubmitError(null);
    setSuccessMessage(null);
    setCrystalBallResult(null);

    if (!province || !provinceId || !worldId) return;

    if (!selectedSpell) {
      reportError('Please select a spell to cast');
      return;
    }

    const spell = getSpellByType(selectedSpell);
    if (!spell) {
      reportError('Invalid spell selected');
      return;
    }

    if (magicData && runeCost > magicData.currentRunes) {
      reportError(`Insufficient runes (need ${runeCost}, have ${magicData.currentRunes})`);
      return;
    }

    if (spell.category === 'OFFENSIVE' && !selectedTarget) {
      reportError('Please select a target province for offensive spells');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await castSpell(worldId, provinceId, {
        spellType: selectedSpell,
        targetProvinceId: selectedTarget || undefined,
      });

      // SF-181 Component 5: branches on the explicit outcome discriminator instead of the old
      // binary result.success — a fizzle/repel/block are all success:false but need distinct
      // tone/copy, not the generic reportError treatment.
      switch (result.outcome) {
        case 'SUCCESS':
          if (result.intelReportId) {
            setCrystalBallResult(result);
          } else {
            reportSuccess(result.message);
          }
          break;
        case 'FIZZLE':
          reportWarning(`${spell.name} fizzled — ${result.runesConsumed ?? 0} runes spent.`);
          break;
        case 'MYSTIC_AURA_REPEL':
          reportWarning('Your spell was repelled by Mystic Aura — their shield is now spent.');
          break;
        case 'NATURES_BLESSING_BLOCK':
          reportWarning("Blocked by the target's Nature's Blessing.");
          break;
        default:
          // Defensive fallback for a missing/unrecognized outcome (e.g. a cached pre-SF-181
          // response with no outcome field) — fall back to the old binary result.success check
          // rather than assuming failure outright.
          if (result.success) {
            if (result.intelReportId) {
              setCrystalBallResult(result);
            } else {
              reportSuccess(result.message);
            }
          } else {
            reportError(result.message);
          }
      }

      // Fizzles still consume runes/mana and can lose wizards — refresh unconditionally so the
      // UI picks up those changes, not just on the success branch.
      await refresh();
    } catch (err) {
      reportError(err instanceof Error ? err.message : 'Failed to cast spell');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !province || !magicData) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <ErrorMessage message={error || 'Province data not available'} />
        </div>
      </div>
    );
  }

  const shellProps: MagicShellProps = {
    province,
    magicData,
    kingdomNumber,
    islandNumber,
    setKingdomNumber,
    setIslandNumber,
    handleChangeKingdom,
    isLoadingKingdom,
    kingdomMembers,
    selectedTarget,
    handleTargetChange,
    selectedTargetLabel,
    selectedTargetProtectionCountdown,
    spellGroups,
    selectedSpell,
    handleSpellChange,
    runeCost,
    isSubmitting,
    submitError,
    successMessage,
    castBlockedReason,
    handleCastSpell,
    crystalBallResult,
  };

  return (
    <div className="space-y-4">
      {platform === 'mobile' ? <MobileMagicShell {...shellProps} /> : <DesktopMagicShell {...shellProps} />}

      {/* Crystal Eye Result (SF-91d) — has crystalEye payload, render directly */}
      {crystalBallResult && crystalBallResult.crystalEye && (
        <CrystalEyeResult
          runesConsumed={crystalBallResult.runesConsumed || 0}
          manaConsumed={crystalBallResult.manaConsumed || 0}
          data={crystalBallResult.crystalEye}
        />
      )}

      {/* Crystal Ball Result (legacy intel-spell path — fetches from backend) */}
      {crystalBallResult && crystalBallResult.intelReportId && !crystalBallResult.crystalEye && (
        <CrystalBallResult
          intelReportId={crystalBallResult.intelReportId}
          runesConsumed={crystalBallResult.runesConsumed || 0}
          manaConsumed={crystalBallResult.manaConsumed || 0}
        />
      )}
    </div>
  );
}
